import {
  startHandoffTracking,
  saveHandoffTracking,
  clearHandoffTracking,
} from '~publish/helpers/publicTracking/handoff'
import {
  getPublicSignupAttribution,
  trackPublicDraftHandoff,
} from '~publish/helpers/publicTracking'
import { useEffect, useRef, useState } from 'react'
import { useLocation, useNavigate, useNavigationType } from 'react-router-dom'

import type { ApolloClient } from '@apollo/client'
import { useApolloClient } from '@apollo/client/react'

import { UploaderRestrictions } from '@buffer-mono/uploader'
import {
  Flex,
  SimpleSpinner,
  toast,
  useCallbackRef,
} from '@buffer-mono/popcorn'

import {
  ContentItemCard_ContentItem,
  mapDraftContentToPrefillMedia,
} from '~publish/components/ContentItemCard'
import type { DraftContentInput } from '~publish/components/Composer/save/graphqlTypes'
import { DRAFT_LIST_REFETCH_QUERIES } from '~publish/components/Composer/save/draftListRefetchQueries'
import { mapGif, mapImage } from '~publish/components/Composer/save/mapAssets'
import { useAccount, useOrganizationId } from '~publish/contexts/AccountContext'
import { graphql, readFragment } from '~publish/graphql'
import type { FragmentOf } from '~publish/graphql'
import {
  GUEST_DRAFT_HANDOFF_LANDING_PATH,
  GUEST_DRAFT_HANDOFF_QUERY_PARAM,
  getBrowserGuestDraftHandoffStore,
  getGuestDraftHandoffRedirectPath,
} from '~publish/helpers/guestDraftHandoff'
import type { GuestDraftSignupReturnPath } from '~publish/helpers/guestDraftHandoff'
import { connectChannel } from '~publish/helpers/channelConnections'
import { getBrowserGuestDraftStore } from '~publish/helpers/guestDrafts'
import type { GuestDraft } from '~publish/helpers/guestDrafts'
import {
  GUEST_MEDIA_MAX_COUNT,
  GUEST_MEDIA_UPLOAD_CONFIG,
} from '~publish/helpers/guestMedia'
import type { GuestMedia } from '~publish/helpers/guestMedia'
import { getGuestConnectChannelService } from '~publish/helpers/guestNetworks'
import { isOfType } from '~publish/helpers/typeGuards'
import { usePostComposer } from '~publish/hooks/usePostComposer'
import type { PrefillPostData } from '~publish/hooks/usePostComposer'
import {
  getNewGif,
  getNewImage,
} from '~publish/legacy/composer/composer/entities/factories'
import usePublishUploader from '~publish/legacy/uploads/hooks/usePublishUploader'

import {
  clearGuestDraftHandoffJournal,
  findJournalDraft,
  hasGuestDraftHandoffJournal,
  readGuestDraftHandoffJournal,
  retainCurrentJournalMedia,
  setJournalContentItemId,
  setJournalMedia,
  writeGuestDraftHandoffJournal,
} from './guestDraftHandoffJournal'
import {
  isGuestDraftImportPromptDismissed,
  setGuestDraftImportPrompt,
} from './guestDraftImportPrompt'
import type {
  GuestDraftHandoffJournal,
  PromotedGuestMedia,
} from './guestDraftHandoffJournal'
import { createGuestTagResolver, GuestTagImportError } from './resolveGuestTags'
import {
  isGuestMediaUnavailableError,
  promoteGuestDraftMedia,
} from './promoteGuestDraftMedia'

const GetGuestDraftHandoffContentItem = graphql(
  `
    query GetGuestDraftHandoffContentItem($input: ContentItemInput!) {
      contentItem(input: $input) {
        id
        ...ContentItemCard_ContentItem
      }
    }
  `,
  [ContentItemCard_ContentItem],
)

const CreateGuestDraftHandoffContentItemDraft = graphql(
  `
    mutation CreateGuestDraftHandoffContentItemDraft(
      $input: CreateContentItemDraftInput!
    ) {
      createContentItemDraft(input: $input) {
        __typename
        ... on CreateContentItemDraftSuccess {
          contentItem {
            id
            ...ContentItemCard_ContentItem
          }
        }
        ... on CreateContentItemDraftFailure {
          message
          errors {
            message
          }
        }
      }
    }
  `,
  [ContentItemCard_ContentItem],
)

const UpdateGuestDraftHandoffContentItemDraft = graphql(
  `
    mutation UpdateGuestDraftHandoffContentItemDraft(
      $input: UpdateContentItemDraftInput!
    ) {
      updateContentItemDraft(input: $input) {
        __typename
        ... on UpdateContentItemDraftSuccess {
          contentItem {
            id
            ...ContentItemCard_ContentItem
          }
        }
        ... on UpdateContentItemDraftFailure {
          message
          errors {
            ... on MutationError {
              message
            }
          }
        }
      }
    }
  `,
  [ContentItemCard_ContentItem],
)

const UPLOADER_RESTRICTIONS = UploaderRestrictions.new({
  maxNumberOfFiles: GUEST_MEDIA_MAX_COUNT,
  uploadConfig: GUEST_MEDIA_UPLOAD_CONFIG,
})
const getUploadCount = (): number => 0

function removeHandoffQuery(intentId: string): void {
  const url = new URL(window.location.href)
  if (url.searchParams.get(GUEST_DRAFT_HANDOFF_QUERY_PARAM) !== intentId) {
    return
  }

  url.searchParams.delete(GUEST_DRAFT_HANDOFF_QUERY_PARAM)
  window.history.replaceState(
    window.history.state,
    '',
    `${url.pathname}${url.search}${url.hash}`,
  )
}

function hasHandoffQuery(intentId: string): boolean {
  return (
    new URLSearchParams(window.location.search).get(
      GUEST_DRAFT_HANDOFF_QUERY_PARAM,
    ) === intentId
  )
}

function compareDraftsOldestFirst(left: GuestDraft, right: GuestDraft): number {
  if (left.createdAt !== right.createdAt)
    return left.createdAt - right.createdAt
  if (left.updatedAt !== right.updatedAt)
    return left.updatedAt - right.updatedAt
  return left.id.localeCompare(right.id)
}

function mapPromotedMediaToAssets(
  media: readonly PromotedGuestMedia[],
): DraftContentInput['assets'] {
  return media.map((item) => {
    if (item.mimeType === 'image/gif') {
      return {
        image: mapGif(
          getNewGif(
            item.url,
            item.thumbnailUrl,
            item.width ?? null,
            item.height ?? null,
          ),
        ),
      }
    }

    return {
      image: mapImage(
        getNewImage({
          url: item.url,
          width: item.width ?? null,
          height: item.height ?? null,
          altText: item.altText ?? null,
        }),
        false,
      ),
    }
  })
}

function getTargetDate(draft: GuestDraft): string | null {
  return draft.scheduledAt === undefined
    ? null
    : new Date(draft.scheduledAt).toISOString()
}

function mapContentItemToPrefill(
  contentItem: FragmentOf<typeof ContentItemCard_ContentItem>,
): PrefillPostData {
  const item = readFragment(ContentItemCard_ContentItem, contentItem)
  if (!isOfType(item.body, 'DraftContent')) {
    throw new Error('The imported draft has no draft content')
  }

  return {
    text: item.body.text,
    tags: item.tags.map((tag) => tag.id),
    ...(item.targetDate
      ? { dueAt: Math.floor(new Date(item.targetDate).getTime() / 1000) }
      : {}),
    ...mapDraftContentToPrefillMedia(item.body.assets),
  }
}

async function getContentItemPrefill(
  client: ApolloClient,
  contentItemId: string,
  signal: AbortSignal,
): Promise<PrefillPostData> {
  const { data } = await client.query({
    query: GetGuestDraftHandoffContentItem,
    variables: { input: { id: contentItemId } },
    fetchPolicy: 'network-only',
    context: {
      queryDeduplication: false,
      fetchOptions: { signal },
    },
  })
  if (!data) throw new Error('The imported draft could not be loaded')

  return mapContentItemToPrefill(data.contentItem)
}

function findPromotedMedia(
  journal: GuestDraftHandoffJournal,
  draftId: string,
  media: GuestMedia,
): PromotedGuestMedia | undefined {
  return findJournalDraft(journal, draftId)?.media.find(
    (item) =>
      item.guestMediaId === media.id &&
      item.sourceUploadedAt === media.uploadedAt,
  )
}

const GUEST_CONNECT_CHANNEL_CTA = 'directLink-directLink-directLink-1'

export function GuestDraftHandoff(): null {
  const [retryCount, setRetryCount] = useState(0)
  const navigate = useNavigate()
  const location = useLocation()
  const navigationType = useNavigationType()
  const hasNavigatedRef = useRef(false)
  useEffect(() => {
    if (navigationType === 'PUSH') hasNavigatedRef.current = true
  }, [location.key, navigationType])
  const returnFromSignup = useCallbackRef(
    (returnTo: GuestDraftSignupReturnPath): void => {
      const returnUrl = new URL(returnTo, window.location.origin)
      const service = getGuestConnectChannelService(returnUrl.hash)
      if (!service) {
        navigate(returnTo, { replace: true })
        return
      }
      navigate(`${returnUrl.pathname}${returnUrl.search}`, { replace: true })
      connectChannel({ service, cta: GUEST_CONNECT_CHANNEL_CTA })
    },
  )
  const organizationId = useOrganizationId()
  const { account } = useAccount()
  const startBrowserDraftImport = useCallbackRef((): void => {
    setGuestDraftImportPrompt(null)
    const handoffStore = getBrowserGuestDraftHandoffStore()
    const current = handoffStore.read()
    if (
      current.status === 'ready' &&
      !hasGuestDraftHandoffJournal(current.intent.id)
    ) {
      handoffStore.clear(current.intent.id)
    }
    const result = handoffStore.create({
      action: 'signUp',
      returnTo: GUEST_DRAFT_HANDOFF_LANDING_PATH,
    })
    if (result.status !== 'saved') {
      toast.error('The draft import could not be started.')
      return
    }
    navigate(getGuestDraftHandoffRedirectPath(result.intent.id))
    setRetryCount((count) => count + 1)
  })
  const offerBrowserDraftImport = useCallbackRef((): void => {
    if (isGuestDraftImportPromptDismissed()) return
    const organizations = account?.organizations ?? []
    const organizationName =
      organizations.length > 1
        ? organizations.find(
            (organization) => organization.id === organizationId,
          )?.name
        : undefined
    setGuestDraftImportPrompt({
      organizationName,
      startImport: startBrowserDraftImport,
    })
  })
  const client = useApolloClient()
  const { editContentItemDraftInComposer } = usePostComposer()
  const uploader = usePublishUploader({
    id: 'guestDraftHandoff',
    fileRestrictions: UPLOADER_RESTRICTIONS,
    getCustomCount: getUploadCount,
  })

  useEffect(() => {
    setGuestDraftImportPrompt(null)
    if (!organizationId) return

    const handoffId = new URLSearchParams(window.location.search).get(
      GUEST_DRAFT_HANDOFF_QUERY_PARAM,
    )

    const handoffStore = getBrowserGuestDraftHandoffStore()
    const handoffResult = handoffStore.read(handoffId ?? undefined)
    if (handoffResult.status !== 'ready') {
      if (handoffId) {
        toast.warning(
          'This draft import could not be found. Your browser drafts are still saved.',
        )
      } else if (handoffResult.status === 'empty') {
        offerBrowserDraftImport()
      }
      return
    }

    const intent = handoffResult.intent
    const composerIntent = intent.action === 'signUp' ? undefined : intent
    const isHandoffLanding = handoffId === intent.id

    if (
      !handoffId &&
      !hasGuestDraftHandoffJournal(intent.id) &&
      !composerIntent?.importedInitiatingContentItemId
    ) {
      offerBrowserDraftImport()
      return
    }

    const controller = new AbortController()
    const { signal } = controller
    const toastId = `guest-draft-handoff:${intent.id}`
    const progressToastId = `${toastId}:progress`
    const showRetry = (
      message = 'Some browser drafts could not be imported.',
    ): void => {
      toast.dismiss(progressToastId)
      toast.error(message, {
        id: toastId,
        duration: Infinity,
        actions: [
          {
            label: 'Retry',
            onClick: (): void => setRetryCount((count) => count + 1),
          },
          {
            label: 'Stop import',
            onClick: (): void => {
              const clearResult = handoffStore.clear(intent.id)
              if (
                clearResult.status === 'cleared' ||
                clearResult.status === 'not-found'
              ) {
                controller.abort()
                clearGuestDraftHandoffJournal(intent.id)
                clearHandoffTracking(intent.id)
                removeHandoffQuery(intent.id)
                toast.dismiss(toastId)
              } else {
                toast.error('The draft import could not be stopped.')
              }
            },
          },
        ],
      })
    }
    toast.dismiss(toastId)

    const run = async (): Promise<void> => {
      if (signal.aborted || handoffStore.read(intent.id).status !== 'ready')
        return
      const tagResolver = createGuestTagResolver(client, organizationId, signal)
      let tagFailureMessage: string | undefined
      const draftStore = getBrowserGuestDraftStore()
      const drafts = draftStore
        .getSnapshot()
        .drafts.toSorted(compareDraftsOldestFirst)
      let journal = readGuestDraftHandoffJournal(intent.id)
      if (journal.organizationId && journal.organizationId !== organizationId) {
        showRetry(
          'Switch back to the organization where you started this import, then retry.',
        )
        return
      }
      if (
        !journal.organizationId &&
        (journal.drafts.length > 0 ||
          composerIntent?.importedInitiatingContentItemId)
      ) {
        showRetry(
          'This import has no saved organization. Stop this import before starting another.',
        )
        return
      }
      journal = { ...journal, organizationId }
      const tracking = startHandoffTracking({
        intentId: intent.id,
        publicSessionId: getPublicSignupAttribution()?.publicSessionId,
        drafts,
        importedDraftIds: journal.drafts.map((draft) => draft.draftId),
        now: Date.now(),
      })
      writeGuestDraftHandoffJournal(journal)
      if (drafts.length > 0) {
        toast(
          <Flex gap="xs" align="center">
            <SimpleSpinner />
            {`Importing ${drafts.length} ${drafts.length === 1 ? 'draft' : 'drafts'}…`}
          </Flex>,
          { id: progressToastId, duration: Infinity, dismissible: false },
        )
      }
      let initiatingContentItemId = composerIntent
        ? (composerIntent.importedInitiatingContentItemId ??
          findJournalDraft(journal, composerIntent.initiatingDraftId)
            ?.contentItemId)
        : undefined
      let initiatingPrefill: PrefillPostData | undefined
      const failedDraftIds: string[] = []
      const unimportableDraftIds: string[] = []
      let hasUnavailableMedia = false

      const promoteMedia = async (
        draft: GuestDraft,
      ): Promise<PromotedGuestMedia[]> => {
        const results = await Promise.allSettled(
          draft.media.map(async (media) => {
            if (signal.aborted) {
              throw new DOMException('Aborted', 'AbortError')
            }

            const existingMedia = findPromotedMedia(journal, draft.id, media)
            if (existingMedia) return existingMedia

            try {
              const promoted = await promoteGuestDraftMedia(
                media,
                uploader,
                signal,
              )
              if (signal.aborted)
                throw new DOMException('Aborted', 'AbortError')
              journal = setJournalMedia(journal, draft.id, promoted)
              writeGuestDraftHandoffJournal(journal)
              return promoted
            } catch (error) {
              if (isGuestMediaUnavailableError(error)) {
                hasUnavailableMedia = true
                if (tracking) {
                  tracking.hasUnavailableMedia = true
                  if (
                    media.expiresAt <= Date.now() &&
                    !tracking.expiredMediaIds.includes(media.id)
                  ) {
                    tracking.expiredMediaIds.push(media.id)
                  }
                  saveHandoffTracking(intent.id, tracking)
                }
                return undefined
              }
              throw error
            }
          }),
        )

        const failedResult = results.find(
          (result): result is PromiseRejectedResult =>
            result.status === 'rejected',
        )
        if (failedResult) throw failedResult.reason

        return results.flatMap((result) =>
          result.status === 'fulfilled' && result.value ? [result.value] : [],
        )
      }

      const processDraft = async (draft: GuestDraft): Promise<void> => {
        if (signal.aborted) return

        try {
          journal = retainCurrentJournalMedia(journal, draft.id, draft.media)
          writeGuestDraftHandoffJournal(journal)
          const journalDraft = findJournalDraft(journal, draft.id)
          const existingContentItemId = journalDraft?.contentItemId
          if (existingContentItemId) {
            let prefill: PrefillPostData | undefined
            if (journalDraft.sourceUpdatedAt !== draft.updatedAt) {
              const promotedMedia = await promoteMedia(draft)
              if (draft.text.trim() === '' && promotedMedia.length === 0) {
                unimportableDraftIds.push(draft.id)
                return
              }

              const tagIds = await tagResolver.resolve(draft.tags)
              signal.throwIfAborted()
              const { data } = await client.mutate({
                mutation: UpdateGuestDraftHandoffContentItemDraft,
                variables: {
                  input: {
                    id: existingContentItemId,
                    ...(tagIds === undefined ? {} : { tagIds }),
                    targetDate: getTargetDate(draft),
                    draft: {
                      text: draft.text,
                      assets: mapPromotedMediaToAssets(promotedMedia),
                      aiAssisted: false,
                    },
                  },
                },
                context: { fetchOptions: { signal } },
              })
              if (signal.aborted) return
              const payload = data?.updateContentItemDraft
              if (payload?.__typename !== 'UpdateContentItemDraftSuccess') {
                throw new Error('The draft could not be imported')
              }

              journal = setJournalContentItemId(
                journal,
                draft.id,
                existingContentItemId,
                draft.updatedAt,
              )
              writeGuestDraftHandoffJournal(journal)
              prefill = mapContentItemToPrefill(payload.contentItem)
            }

            if (
              composerIntent &&
              draft.id === composerIntent.initiatingDraftId
            ) {
              prefill ??= await getContentItemPrefill(
                client,
                existingContentItemId,
                signal,
              )
              const markResult = handoffStore.markInitiatingDraftImported(
                intent.id,
                existingContentItemId,
              )
              if (markResult.status !== 'saved') {
                throw new Error('The draft import progress could not be saved')
              }
              initiatingContentItemId = existingContentItemId
              initiatingPrefill = prefill
            }

            const deleteResult = draftStore.delete(draft.id, draft.updatedAt)
            if (
              deleteResult.status !== 'deleted' &&
              deleteResult.status !== 'not-found'
            ) {
              throw new Error('The imported browser draft could not be removed')
            }
            return
          }

          const promotedMedia = await promoteMedia(draft)
          if (draft.text.trim() === '' && promotedMedia.length === 0) {
            unimportableDraftIds.push(draft.id)
            return
          }

          const tagIds = await tagResolver.resolve(draft.tags)
          signal.throwIfAborted()
          const { data } = await client.mutate({
            mutation: CreateGuestDraftHandoffContentItemDraft,
            variables: {
              input: {
                correlationId: draft.id,
                organizationId,
                ...(tagIds === undefined ? {} : { tagIds }),
                ...(draft.scheduledAt === undefined
                  ? {}
                  : { targetDate: getTargetDate(draft) }),
                draft: {
                  text: draft.text,
                  assets: mapPromotedMediaToAssets(promotedMedia),
                  aiAssisted: false,
                },
              },
            },
            context: { fetchOptions: { signal } },
          })
          if (signal.aborted) return
          const payload = data?.createContentItemDraft
          if (payload?.__typename !== 'CreateContentItemDraftSuccess') {
            throw new Error('The draft could not be imported')
          }

          const contentItemId = payload.contentItem.id
          journal = setJournalContentItemId(journal, draft.id, contentItemId)
          writeGuestDraftHandoffJournal(journal)
          await processDraft(draft)
        } catch (error) {
          if (signal.aborted) return
          if (error instanceof GuestTagImportError)
            tagFailureMessage = error.message
          failedDraftIds.push(draft.id)
        }
      }

      await drafts.reduce<Promise<void>>(
        (previous, draft) => previous.then(() => processDraft(draft)),
        Promise.resolve(),
      )

      if (signal.aborted) return

      if (hasUnavailableMedia) {
        toast.warning('Some images could not be imported.')
      }

      const limitedTagNames = tagResolver.getLimitedTagNames()
      const tagLimitNotice =
        limitedTagNames.length > 0
          ? `plan’s tag limit was reached, so these tags weren’t added: ${limitedTagNames.map((name) => `“${name}”`).join(', ')}.`
          : undefined
      let hasDraftsInBufferNotice = false

      await client.refetchQueries({ include: DRAFT_LIST_REFETCH_QUERIES })

      if (composerIntent && initiatingContentItemId && !initiatingPrefill) {
        try {
          initiatingPrefill = await getContentItemPrefill(
            client,
            initiatingContentItemId,
            signal,
          )
        } catch {
          if (!signal.aborted)
            failedDraftIds.push(composerIntent.initiatingDraftId)
        }
      }

      if (signal.aborted) return

      if (
        drafts.some((draft) => draft.tags?.length) ||
        initiatingPrefill?.tags?.length
      ) {
        await tagResolver.refresh()
      }
      if (signal.aborted) return

      const uniqueFailedDraftIds = new Set(failedDraftIds)
      const uniqueUnimportableDraftIds = new Set(unimportableDraftIds)
      const initiatingDraftMissing = Boolean(
        composerIntent &&
        !initiatingContentItemId &&
        !drafts.some((draft) => draft.id === composerIntent.initiatingDraftId),
      )
      const initiatingDraftFinished =
        !composerIntent ||
        Boolean(initiatingContentItemId) ||
        initiatingDraftMissing ||
        uniqueUnimportableDraftIds.has(composerIntent.initiatingDraftId)

      if (
        isHandoffLanding &&
        composerIntent &&
        initiatingContentItemId &&
        initiatingPrefill &&
        !uniqueFailedDraftIds.has(composerIntent.initiatingDraftId) &&
        !uniqueUnimportableDraftIds.has(composerIntent.initiatingDraftId)
      ) {
        const prefillPostData =
          composerIntent.action === 'schedule' && composerIntent.scheduledAt
            ? { ...initiatingPrefill, dueAt: composerIntent.scheduledAt }
            : initiatingPrefill
        await editContentItemDraftInComposer(
          {
            cta: 'publish-guestDraftHandoff-import-editContentItemDraft-1',
            contentItemId: initiatingContentItemId,
            prefillPostData,
          },
          { draftMode: composerIntent.action === 'saveDraft' },
        )
      }

      toast.dismiss(progressToastId)
      if (signal.aborted) return

      if (uniqueFailedDraftIds.size === 0 && initiatingDraftFinished) {
        const clearResult = handoffStore.clear(intent.id)
        if (
          clearResult.status === 'cleared' ||
          clearResult.status === 'not-found'
        ) {
          if (clearResult.status === 'cleared' && tracking) {
            trackPublicDraftHandoff(tracking.publicSessionId, {
              organizationId,
              hasText: tracking.hasText,
              hasMedia: journal.drafts.some((draft) => draft.media.length > 0),
              promotionSucceeded:
                !tracking.hasUnavailableMedia &&
                tracking.expiredMediaIds.length === 0,
              draftCount: tracking.draftCount,
              draftsImportedCount: journal.drafts.filter(
                (draft) => draft.contentItemId,
              ).length,
              mediaExpiredCount: tracking.expiredMediaIds.length,
            })
          }
          clearGuestDraftHandoffJournal(intent.id)
          clearHandoffTracking(intent.id)
          if (initiatingDraftMissing || uniqueUnimportableDraftIds.size > 0) {
            toast.warning('Some browser drafts could not be imported')
          } else if (drafts.length > 0 && !hasUnavailableMedia) {
            hasDraftsInBufferNotice = true
            if (tagLimitNotice) {
              toast.warning(
                `Your drafts are now in Buffer, but your ${tagLimitNotice}`,
              )
            } else {
              toast.success('Your drafts are now in Buffer')
            }
          }
          if (
            intent.action === 'signUp' &&
            isHandoffLanding &&
            !hasNavigatedRef.current &&
            hasHandoffQuery(intent.id)
          ) {
            returnFromSignup(intent.returnTo)
          }
        } else {
          showRetry()
        }
      } else {
        showRetry(tagFailureMessage)
      }

      if (tagLimitNotice && !hasDraftsInBufferNotice) {
        toast.warning(`Your ${tagLimitNotice}`)
      }
    }

    const importPromise = navigator.locks
      ? navigator.locks.request(
          `buffer:publish:guest-draft-import:${organizationId}`,
          { signal },
          run,
        )
      : run()

    void importPromise
      .catch(() => {
        if (!signal.aborted) {
          showRetry()
        }
      })
      .finally(() => {
        toast.dismiss(progressToastId)
        if (
          !signal.aborted &&
          handoffStore.read(intent.id).status === 'empty'
        ) {
          removeHandoffQuery(intent.id)
        }
      })

    return (): void => {
      controller.abort()
      toast.dismiss(toastId)
      toast.dismiss(progressToastId)
    }
  }, [
    client,
    editContentItemDraftInComposer,
    returnFromSignup,
    offerBrowserDraftImport,
    organizationId,
    uploader,
    retryCount,
  ])

  return null
}
